import { PartialMessage, Timestamp } from '@bufbuild/protobuf';
import {
  endOfDay,
  endOfMonth,
  endOfWeek,
  endOfYear,
  endOfYesterday,
  startOfDay,
  startOfMonth,
  startOfWeek,
  startOfYear,
  startOfYesterday,
  subDays,
  subMonths,
  subWeeks,
  subYears,
} from 'date-fns';

const GMT_TZ_KEY = '__GMT_TZ__';
export const TZ_KEY = 'tz';

export const getTzFromLocalStorage = () => {
  return (
    localStorage.getItem(GMT_TZ_KEY) && Number(localStorage.getItem(GMT_TZ_KEY))
  );
};

export const getTzLocal = () => {
  return -(new Date().getTimezoneOffset() / 60);
};

export const setTzToLocalStorage = (tz: number) => {
  return localStorage.setItem(GMT_TZ_KEY, `${tz}`);
};

export const getCurrentTzNumber = () => getTzFromLocalStorage() || getTzLocal();

const A_THOUSAND = 1_000;

export enum PeriodType {
  Default = '',
  Daily = 'daily',
  Weekly = 'weekly',
  Monthly = 'monthly',
  Yesterday = 'yesterday',
  ThisWeek = 'thisWeek',
  LastWeek = 'lastWeek',
  Last4Weeks = 'last4Weeks',
  ThisMonth = 'thisMonth',
  LastMonth = 'lastMonth',
  Last3Months = 'last3Months',
  Last7Days = 'last7Days',
  Last30Days = 'last30Days',
  Last6Months = 'last6Months',
  ThisYear = 'thisYear',
  LastYear = 'lastYear',
  Last3Years = 'last3Years',
  Custom = 'custom',
}

const convertNumberToGMT = (n: number) => {
  if (n > 0) return `Etc/GMT-${n}`;
  if (n < 0) return `Etc/GMT+${n}`;
  return 'Etc/GMT';
};

export const formatTimeStampToObject = (
  timestamp: number,
  nanos: number = 0,
) => ({
  seconds: Math.floor(timestamp / 1000),
  nanos,
});

export const formatWithSchema = (
  timestamp: number,
  options?: Intl.DateTimeFormatOptions,
): string => {
  const currentTzNumber = getCurrentTzNumber();
  const optionsDefault: Intl.DateTimeFormatOptions = {
    timeZone: convertNumberToGMT(currentTzNumber),
    hourCycle: 'h23',
  };

  return new Intl.DateTimeFormat('en-US', {
    ...optionsDefault,
    ...options,
  }).format(timestamp);
};

export const formatDate = (
  milliseconds?: number,
  options?: Intl.DateTimeFormatOptions,
): string => {
  if (!milliseconds) return '-';

  return formatWithSchema(milliseconds, {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    ...options,
  });
};

export const formatDateMonth = (
  milliseconds?: number,
  options?: Intl.DateTimeFormatOptions,
): string => {
  if (!milliseconds) return '-';

  return formatWithSchema(milliseconds, {
    year: 'numeric',
    month: '2-digit',
    ...options,
  });
};

export const formatDateYear = (
  milliseconds?: number,
  options?: Intl.DateTimeFormatOptions,
): string => {
  if (!milliseconds) return '-';

  return formatWithSchema(milliseconds, {
    year: 'numeric',
    ...options,
  });
};

export const formatDateTime = (
  milliseconds?: number,
  seconds?: boolean,
): string => {
  if (!milliseconds) return '-';

  if (!!seconds)
    return formatWithSchema(milliseconds, {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    });

  return formatWithSchema(milliseconds, {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
};

export const getYear = (milliseconds?: number): string => {
  if (!milliseconds) return '-';

  return formatWithSchema(milliseconds, {
    year: 'numeric',
  });
};

export const getMonth = (milliseconds?: number): string => {
  if (!milliseconds) return '-';

  return formatWithSchema(milliseconds, {
    month: '2-digit',
  });
};

export const getDayTz = (timestamp: number): string =>
  formatWithSchema(timestamp, {
    day: '2-digit',
  });

export const convertMillisecondsToSeconds = (milliseconds: number) => {
  if (!milliseconds) return 0;

  return Math.trunc(milliseconds / A_THOUSAND);
};

export const convertSecondsToMilliseconds = (seconds?: number) => {
  if (!seconds) return 0;

  return seconds * A_THOUSAND;
};

export const splitDateTime = (milliseconds?: number) => {
  if (!milliseconds)
    return {
      year: 0,
      month: 0,
      day: 0,
      hour: undefined,
      minute: undefined,
    };

  const d = formatDateTime(milliseconds);
  const [date, time] = d.split(', ');
  const [month, day, year] = date.split('/');
  const [hour, minute] = time.split(':');

  return {
    year: +year,
    month: +month - 1,
    day: +day,
    hour: +hour,
    minute: +minute,
  };
};

export const getTimezoneOffset = () => {
  const tzLocal = getTzLocal();
  const tzCurrent = getCurrentTzNumber();
  const offset = (tzLocal - tzCurrent) * 60 * 60 * A_THOUSAND;
  return offset;
};

export const setTimezoneInTime = (timestamp: number) => {
  if (timestamp <= 0) return 0;
  const offset = getTimezoneOffset();
  return timestamp + offset;
};

export const removeTimezoneInTime = (timestamp: number) => {
  if (timestamp <= 0) return 0;
  const offset = getTimezoneOffset();
  return timestamp - offset;
};

export const getNowTz = (): number => {
  return setTimezoneInTime(Date.now().valueOf());
};

export const getCurrentDay = () => {
  return {
    start: setTimezoneInTime(Math.floor(+startOfDay(new Date()))),
    end: setTimezoneInTime(Math.floor(+endOfDay(new Date()))),
  };
};

export const getYesterday = () => {
  return {
    start: setTimezoneInTime(Math.floor(+startOfYesterday())),
    end: setTimezoneInTime(Math.floor(+endOfYesterday())),
  };
};

export const getLast7Days = (options?: { showCurrentDate?: boolean }) => {
  const { showCurrentDate } = options || {};
  const now = new Date();
  const dateOfLast7Days = subDays(now, showCurrentDate ? 6 : 7);
  return {
    start: setTimezoneInTime(Math.floor(+startOfDay(dateOfLast7Days))),
    end: setTimezoneInTime(Math.floor(+endOfDay(now))),
  };
};

export const getLast30Days = () => {
  const dateOfLast30Days = subDays(new Date(), 30);
  return {
    start: setTimezoneInTime(Math.floor(+startOfDay(dateOfLast30Days))),
    end: setTimezoneInTime(Math.floor(+endOfDay(new Date()))),
  };
};

export const getLastWeek = () => {
  const dateOfLastWeek = subDays(new Date(), 7);
  return {
    start: setTimezoneInTime(+startOfWeek(dateOfLastWeek, { weekStartsOn: 1 })),
    end: setTimezoneInTime(+endOfWeek(dateOfLastWeek, { weekStartsOn: 1 })),
  };
};

export const getThisWeek = () => {
  const date = new Date();
  return {
    start: setTimezoneInTime(+startOfWeek(date, { weekStartsOn: 1 })),
    end: setTimezoneInTime(+endOfWeek(date, { weekStartsOn: 1 })),
  };
};

export const getLast4Weeks = () => {
  const dateOfLast4Weeks = subWeeks(new Date(), 4);
  return {
    start: setTimezoneInTime(
      +startOfWeek(dateOfLast4Weeks, { weekStartsOn: 1 }),
    ),
    end: setTimezoneInTime(Math.floor(+endOfDay(new Date()))),
  };
};

export const getThisMonth = () => {
  const date = new Date();
  return {
    start: setTimezoneInTime(startOfMonth(date).valueOf()),
    end: setTimezoneInTime(endOfMonth(date).valueOf()),
  };
};

export const getLastMonth = () => {
  const lastMonth = subMonths(new Date(), 1);
  return {
    start: setTimezoneInTime(startOfMonth(lastMonth).valueOf()),
    end: setTimezoneInTime(endOfMonth(lastMonth).valueOf()),
  };
};

export const getLast3Months = () => {
  const dateOfLast3Months = subMonths(new Date(), 3);
  return {
    start: setTimezoneInTime(startOfMonth(dateOfLast3Months).valueOf()),
    end: setTimezoneInTime(endOfMonth(new Date()).valueOf()),
  };
};

export const getLast6Months = () => {
  const dateOfLast6Months = subMonths(new Date(), 6);

  return {
    start: setTimezoneInTime(startOfMonth(dateOfLast6Months).valueOf()),
    end: setTimezoneInTime(endOfMonth(new Date()).valueOf()),
  };
};

export const getThisYear = () => {
  const date = new Date();
  return {
    start: setTimezoneInTime(+startOfYear(date)),
    end: setTimezoneInTime(+endOfYear(date)),
  };
};

export const getLastYear = () => {
  const lastYear = subYears(new Date(), 1);
  return {
    start: setTimezoneInTime(+startOfYear(lastYear)),
    end: setTimezoneInTime(+endOfYear(lastYear)),
  };
};

export const getLast3Years = () => {
  const dateOfLast3Years = subYears(new Date(), 3);
  return {
    start: setTimezoneInTime(Math.floor(+startOfDay(dateOfLast3Years))),
    end: setTimezoneInTime(Math.floor(+endOfDay(new Date()))),
  };
};

export const getLastCustomDays = (days: number) => {
  const dateOfLastCustomDays = subDays(new Date(), days);
  return {
    start: setTimezoneInTime(Math.floor(+startOfDay(dateOfLastCustomDays))),
    end: setTimezoneInTime(Math.floor(+endOfDay(new Date()))),
  };
}

export const getLastCustomMonths = (months: number) => {
  const dateOfLastCustomMonths = subMonths(new Date(), months);
  return {
    start: setTimezoneInTime(startOfMonth(dateOfLastCustomMonths).valueOf()),
    end: setTimezoneInTime(endOfMonth(new Date()).valueOf()),
  };
}

export const getLastCustomYears = (years: number) => {
  const dateOfLastCustomYears = subYears(new Date(), years);
  return {
    start: setTimezoneInTime(+startOfYear(dateOfLastCustomYears)),
    end: setTimezoneInTime(+endOfYear(dateOfLastCustomYears)),
  };
}

type MonthHalf = 'first' | 'second';

export const getMonthRange = (
  offsetMonth: number = 0,
  half: MonthHalf = 'first'
) => {
  const now = new Date();
  const targetMonth = new Date(now.getFullYear(), now.getMonth() + offsetMonth, 1);
  const year = targetMonth.getFullYear();
  const month = targetMonth.getMonth();

  if (half === 'first') {
    return {
      start: setTimezoneInTime(new Date(year, month, 1).valueOf()),
      end: setTimezoneInTime(new Date(year, month, 15, 23, 59, 59, 999).valueOf()),
    };
  } else {
    const lastDay = new Date(year, month + 1, 0).getDate();
    return {
      start: setTimezoneInTime(new Date(year, month, 16).valueOf()),
      end: setTimezoneInTime(new Date(year, month, lastDay, 23, 59, 59, 999).valueOf()),
    };
  }
};

export const setTimestampPB = (
  milliseconds: number,
): PartialMessage<Timestamp> => {
  if (!milliseconds) return undefined;
  return {
    nanos: 0,
    seconds: BigInt(convertMillisecondsToSeconds(milliseconds)),
  };
};


export const getRangeByPeriod = (
  period: PeriodType,
): { start: number; end: number } => {
  switch (period) {
    case PeriodType.Daily:
      return getCurrentDay();
    case PeriodType.Yesterday:
      return getYesterday();
    case PeriodType.Last7Days:
      return getLast7Days();
    case PeriodType.Last30Days:
      return getLast30Days();

    case PeriodType.Weekly:
      return getThisWeek();
    case PeriodType.ThisWeek:
      return getThisWeek();
    case PeriodType.LastWeek:
      return getLastWeek();
    case PeriodType.Last4Weeks:
      return getLast4Weeks();

    case PeriodType.Monthly:
      return getThisMonth();
    case PeriodType.ThisMonth:
      return getThisMonth();
    case PeriodType.LastMonth:
      return getLastMonth();
    case PeriodType.Last3Months:
      return getLast3Months();
    case PeriodType.Last6Months:
      return getLast6Months();

    case PeriodType.ThisYear:
      return getThisYear();
    case PeriodType.LastYear:
      return getLastYear();
    case PeriodType.Last3Years:
      return getLast3Years();

    default:
      throw new Error(`missing get range function for period: ${period}`);
  }
};

export const convertTimestampToDateInTimezone = (timestamp: number): Date => {
  const date = new Date(timestamp);
  const offset = getCurrentTzNumber();

  const utcTime = date.getTime() + date.getTimezoneOffset() * 60000;

  const timezoneDate = new Date(utcTime + offset * 3600000);
  return timezoneDate;
};

